import { KeyboardEvent, useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Alternativ } from '../../../api/reise';

interface Props {
  id: string;
  /** Menyen med profiler fra innholdet. */
  meny: Alternativ[];
  /** Kunden kan legge til egne profiler (spørsmålet har «Annet»). */
  egne: boolean;
  valgte: string[];
  onEndre: (valgte: string[]) => void;
}

export const MAKS_PROFILNAVN = 100;

/**
 * Scenario-spørsmål for ordreprofiler: kunden velger profilene fra menyen, og kan legge til egne. Etterpå
 * kommer underspørsmålene for hver profil, ett om gangen (reisemotoren, T021).
 */
export default function Profilvelger({ id, meny, egne, valgte, onEndre }: Props) {
  const { t } = useTranslation();
  const [ny, setNy] = useState('');
  const [feil, setFeil] = useState<string | null>(null);

  const fraMeny = new Set(meny.map((a) => a.verdi));
  const egneValgt = valgte.filter((v) => !fraMeny.has(v));

  function veksle(verdi: string) {
    onEndre(valgte.includes(verdi) ? valgte.filter((v) => v !== verdi) : [...valgte, verdi]);
  }

  function leggTil() {
    const navn = ny.trim();
    if (!navn) return;
    const finnes = [...valgte, ...meny.map((a) => a.tekst)].some((v) => v.toLocaleLowerCase() === navn.toLocaleLowerCase());
    if (finnes) return setFeil('reise.kontroll.profilFinnes');
    if (navn.length > MAKS_PROFILNAVN) return setFeil('reise.kontroll.profilForLang');
    setFeil(null);
    setNy('');
    onEndre([...valgte, navn]);
  }

  // Enter i feltet legger til profilen, i stedet for å sende skjemaet
  function tast(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter') {
      e.preventDefault();
      leggTil();
    }
  }

  return (
    <div>
      <p className="mb-4 text-slate-600">{t('reise.profiler.forklaring')}</p>
      <fieldset aria-labelledby={`${id}-tekst`}>
        <div className="flex flex-col gap-2">
          {meny.map((a) => {
            const valgt = valgte.includes(a.verdi);
            return (
              <label
                key={a.verdi}
                className={
                  'valg ' + (valgt ? 'valg-valgt' : '')
                }
              >
                <input type="checkbox" checked={valgt} onChange={() => veksle(a.verdi)} className="h-[1.125rem] w-[1.125rem] shrink-0" />
                <span className="flex-1">{a.tekst}</span>
              </label>
            );
          })}
        </div>
      </fieldset>

      {egne && (
        <div className="mt-4">
          {egneValgt.length > 0 && (
            <ul className="mb-3 flex flex-wrap gap-2">
              {egneValgt.map((profil) => (
                <li key={profil} className="flex items-center gap-1 rounded-full border border-aksent-200 bg-aksent-50 py-1 pl-3 pr-1 text-aksent-900">
                  <span>{profil}</span>
                  <button
                    type="button"
                    onClick={() => onEndre(valgte.filter((v) => v !== profil))}
                    aria-label={t('reise.profiler.fjern', { profil })}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-slate-200"
                  >
                    <X aria-hidden="true" className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <label htmlFor={`${id}-egen`} className="mb-1.5 block text-sm font-medium text-slate-700">
            {t('reise.profiler.egen')}
          </label>
          <p id={`${id}-egen-hjelp`} className="mb-1 text-sm text-slate-600">
            {t('reise.profiler.egenHjelp')}
          </p>
          <div className="flex flex-col gap-2 sm:flex-row">
            <input
              id={`${id}-egen`}
              type="text"
              value={ny}
              maxLength={MAKS_PROFILNAVN + 1}
              aria-describedby={`${id}-egen-hjelp`}
              onChange={(e) => {
                setFeil(null);
                setNy(e.target.value);
              }}
              onKeyDown={tast}
              className="felt w-full sm:w-80"
            />
            <button type="button" onClick={leggTil} className="knapp-sekundaer">
              {t('reise.profiler.leggTil')}
            </button>
          </div>
          {feil && (
            <p role="alert" className="mt-2 text-sm text-red-800">
              {t(feil)}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
